import type { ApiResponse, MutationResult } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import type { PartnerCreditRepository } from './repository';
import type { PartnerCreditConfiguration, PartnerCreditVersion } from './types';

type IntegrationResponse = ApiResponse<'/api/v1/partners/{partnerId}/credit-integration', 'get'>;
type VersionListResponse = ApiResponse<
  '/api/v1/partners/{partnerId}/credit-integration/versions',
  'get'
>;
type PreviewResponse = ApiResponse<
  '/api/v1/partners/{partnerId}/credit-integration/preview',
  'post'
>;
type ApprovalResponse = ApiResponse<'/api/v1/partners/{partnerId}/credit-integration', 'post'>;
type CertificationResponse = ApiResponse<
  '/api/v1/partners/{partnerId}/credit-integration/certify',
  'post'
>;

export function createRealPartnerCreditRepository(
  client: ApiClient,
  accessToken: string | null,
): PartnerCreditRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async get(partnerId, signal) {
      const response = await client.get<IntegrationResponse>(integrationPath(partnerId), {
        ...auth,
        signal,
      });
      return sanitizeIntegration(response.data.data);
    },
    async listVersions(partnerId, signal) {
      const response = await client.get<VersionListResponse>(
        `${integrationPath(partnerId)}/versions`,
        { ...auth, signal },
      );
      return response.data.data.map(sanitizeVersion);
    },
    async preview(partnerId, input) {
      const response = await client.post<PreviewResponse>(
        `${integrationPath(partnerId)}/preview`,
        input,
        auth,
      );
      return {
        configuration: sanitizePublicConfiguration(response.data.data.configuration),
        previewHash: response.data.data.previewHash,
        certificationRequired: response.data.data.certificationRequired,
      };
    },
    async createVersion(partnerId, input, idempotencyKey) {
      const response = await client.post<ApprovalResponse>(integrationPath(partnerId), input, {
        ...auth,
        idempotencyKey: requireUuidV4(idempotencyKey),
      });
      return mutation(response.data.data, response.requestId);
    },
    async certify(partnerId, input) {
      const response = await client.post<CertificationResponse>(
        `${integrationPath(partnerId)}/certify`,
        input,
        auth,
      );
      return mutation(response.data.data, response.requestId);
    },
    async changeStatus(partnerId, input) {
      const response = await client.post<IntegrationResponse>(
        `${integrationPath(partnerId)}/status`,
        input,
        auth,
      );
      return mutation(sanitizeIntegration(response.data.data), response.requestId);
    },
  };
}

function integrationPath(partnerId: string): string {
  return `/api/v1/partners/${encodeURIComponent(partnerId)}/credit-integration`;
}

function sanitizeIntegration(value: PartnerCreditConfiguration): PartnerCreditConfiguration {
  return {
    id: value.id,
    partnerId: value.partnerId,
    name: value.name,
    status: value.status,
    currentVersion: value.currentVersion === null ? null : sanitizeVersion(value.currentVersion),
    createdAt: value.createdAt,
    createdBy: value.createdBy,
    updatedAt: value.updatedAt,
    updatedBy: value.updatedBy,
  };
}

function sanitizeVersion(value: PartnerCreditVersion): PartnerCreditVersion {
  return {
    id: value.id,
    version: value.version,
    configuration: sanitizePublicConfiguration(value.configuration),
    effectiveFrom: value.effectiveFrom,
    effectiveTo: value.effectiveTo,
    certifiedAt: value.certifiedAt,
    publishedAt: value.publishedAt,
    publishedBy: value.publishedBy,
  };
}

function sanitizePublicConfiguration(value: PartnerCreditVersion['configuration']) {
  return {
    name: value.name,
    creditEndpoint: value.creditEndpoint,
    statusEndpoint: value.statusEndpoint,
    healthEndpoint: value.healthEndpoint,
    ...(value.transactionResultEndpoint === undefined
      ? {}
      : { transactionResultEndpoint: value.transactionResultEndpoint }),
    authType: value.authType,
    ...(value.hmacKeyId === undefined ? {} : { hmacKeyId: value.hmacKeyId }),
    ...(value.oauthTokenEndpoint === undefined
      ? {}
      : { oauthTokenEndpoint: value.oauthTokenEndpoint }),
    ...(value.oauthClientId === undefined ? {} : { oauthClientId: value.oauthClientId }),
    ...(value.oauthScopes === undefined ? {} : { oauthScopes: [...value.oauthScopes] }),
    ...(value.oauthAudience === undefined ? {} : { oauthAudience: value.oauthAudience }),
    timeoutMilliseconds: value.timeoutMilliseconds,
    retryPolicy: {
      maximumAttempts: value.retryPolicy.maximumAttempts,
      initialDelaySeconds: value.retryPolicy.initialDelaySeconds,
      maximumDelaySeconds: value.retryPolicy.maximumDelaySeconds,
      multiplier: value.retryPolicy.multiplier,
      jitterPercent: value.retryPolicy.jitterPercent,
    },
  };
}

function mutation<Value>(data: Value, requestId: string | null): MutationResult<Value> {
  return { data, requestId };
}

function requireUuidV4(value: string): string {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value))
    throw new Error('A UUID v4 idempotency key is required');
  return value;
}
